/**
 * Deterministic, rule-based order parser — NO AI.
 * Turns a free-text Romanian WhatsApp message into priced order lines.
 * Framed in the UI as "recunoaștere automată pe bază de reguli".
 *
 * parseOrder(text, produse, opts?) -> { linii, nemapate }
 */

import type { Produs } from '@/db/dexie'

export type UM = 'kg' | 'buc' | 'tavă'

export interface ParsedLine {
  produsId: string
  cod: string
  nume: string
  cantitateKg: number
  um: UM
  pretKg: number
  matchedText: string
  confidence: number
}

export interface ParseResult {
  linii: ParsedLine[]
  nemapate: string[]
}

export interface ParseOptions {
  /** client-specific per-product price overrides, keyed by produsId */
  pretSpecial?: Record<string, number>
}

// --- Romanian synonym dictionary → SKU cod -------------------------------
// Longest / most-specific phrase wins (see matching below).
export interface SynonymEntry {
  cod: string
  phrases: string[]
}

export const SYNONYMS: SynonymEntry[] = [
  { cod: 'P01', phrases: ['piept dezosat', 'piept fara os', 'piept de pui', 'piept pui', 'piept'] },
  { cod: 'P02', phrases: ['piept cu os'] },
  {
    cod: 'P03',
    phrases: ['pulpe superioare', 'pulpa superioara', 'pulpe sup', 'spate', 'pulpe spate'],
  },
  {
    cod: 'P04',
    phrases: ['pulpe inferioare', 'pulpa inferioara', 'pulpe inf', 'copane', 'copan'],
  },
  { cod: 'P05', phrases: ['aripioare', 'aripi', 'aripa'] },
  { cod: 'P06', phrases: ['tacamuri de pui', 'tacamuri', 'tacam'] },
  { cod: 'P07', phrases: ['pui intreg', 'pui intregi', 'gaina', 'pui'] },
  { cod: 'P08', phrases: ['ficatei de pui', 'ficatei', 'ficat'] },
  { cod: 'P09', phrases: ['pipote', 'pipota'] },
  { cod: 'P10', phrases: ['gat de pui', 'gaturi', 'gat'] },
  { cod: 'P11', phrases: ['carcasa pui', 'carcasa', 'carcase'] },
  { cod: 'P12', phrases: ['snitel de pui', 'snitele', 'snitel'] },
  { cod: 'P13', phrases: ['pulpe dezosate', 'pulpa dezosata'] },
  { cod: 'P14', phrases: ['spinari', 'spinare', 'supa', 'tacamuri supa'] },
]

// A phrase is "specific" (confidence 1.0) when it has 2+ words; single bare
// words that are inherently ambiguous get a lower confidence.
const LOW_CONFIDENCE_WORDS = new Set([
  'piept',
  'pui',
  'ficat',
  'gat',
  'supa',
  'aripa',
  'spate',
  'copan',
])

// Segments that are pure pleasantries / noise — dropped, never "nemapate".
const NOISE = [
  'buna',
  'ziua',
  'salut',
  'buna ziua',
  'multumesc',
  'multam',
  'merci',
  'va rog',
  'te rog',
  'pt',
  'pentru',
  'maine',
  'azi',
  'astazi',
  'urgent',
  'comanda',
  'as dori',
  'as vrea',
  'am nevoie',
  'de',
  'si',
  'la',
  'cu',
  'ora',
  'buna seara',
  'noapte buna',
  'cat mai repede',
]

const KG_UNITS = ['kg', 'kilograme', 'kilogram', 'kile', 'kile']
const BUC_UNITS = ['buc', 'bucati', 'bucata', 'bucăți']
const TAVA_UNITS = ['tavi', 'tava', 'tave', 'tavă', 'tăvi', 'tavă']

const TAVA_KG = 2 // one tray of ficăței ≈ 2 kg
const BUC_KG: Record<string, number> = { P07: 1.6 } // pui întreg ≈ 1.6 kg/buc

export function stripDiacritice(s: string): string {
  return (
    s
      .replace(/ș|ş/g, 's')
      .replace(/ț|ţ/g, 't')
      .replace(/ă/g, 'a')
      .replace(/â/g, 'a')
      .replace(/î/g, 'i')
      .replace(/Ș|Ş/g, 's')
      .replace(/Ț|Ţ/g, 't')
      .replace(/Ă/g, 'a')
      .replace(/Â/g, 'a')
      .replace(/Î/g, 'i')
      .normalize('NFD')
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: intentional \u2014 strip combining diacritical marks after NFD normalization
      .replace(/[\u0300-\u036f]/g, '')
  )
}

export function normalizeText(s: string): string {
  return stripDiacritice(s.toLowerCase()).replace(/\s+/g, ' ').trim()
}

/** All dictionary phrases, normalized, sorted longest-first for greedy match. */
const PHRASE_INDEX: { phrase: string; cod: string; words: number }[] = SYNONYMS.flatMap((e) =>
  e.phrases.map((p) => {
    const phrase = normalizeText(p)
    return { phrase, cod: e.cod, words: phrase.split(' ').length }
  }),
).sort((a, b) => b.phrase.length - a.phrase.length)

const QTY_RE = new RegExp(
  `(\\d+(?:[.,]\\d+)?)\\s*(${[...KG_UNITS, ...BUC_UNITS, ...TAVA_UNITS].join('|')})?`,
  'i',
)

function classifyUnit(raw: string | undefined): UM | null {
  if (!raw) return null
  const u = raw.toLowerCase()
  if (KG_UNITS.includes(u)) return 'kg'
  if (BUC_UNITS.includes(u)) return 'buc'
  if (TAVA_UNITS.includes(u)) return 'tavă'
  return null
}

function splitSegments(normalized: string): string[] {
  return normalized
    .split(/[\n,;]+|\bsi\b|\+|·|•/g)
    .map((s) => s.trim())
    .filter(Boolean)
}

function isNoiseOnly(segment: string): boolean {
  const cleaned = segment
    .replace(/[0-9.,:]+/g, ' ')
    .replace(/\b(kg|kilograme?|buc|bucati|bucata|tavi|tava|tave)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!cleaned) return true
  const words = cleaned.split(' ').filter(Boolean)
  return words.every((w) => NOISE.includes(w))
}

function cleanLeftover(segment: string): string {
  return segment
    .replace(/[0-9.,:]+/g, ' ')
    .replace(/\b(kg|kilograme?|buc|bucati|bucata|tavi|tava|tave|pt|pentru|maine|azi)\b/g, ' ')
    .replace(/[!?.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function parseOrder(text: string, produse: Produs[], opts: ParseOptions = {}): ParseResult {
  const byCod = new Map(produse.map((p) => [p.cod, p]))
  const linii: ParsedLine[] = []
  const nemapate: string[] = []

  const segments = splitSegments(normalizeText(text))

  for (const segment of segments) {
    // 1) quantity + unit
    const qtyMatch = segment.match(QTY_RE)
    const rawQty = qtyMatch?.[1]
    const cantitate = rawQty ? Number.parseFloat(rawQty.replace(',', '.')) : null
    const um = classifyUnit(qtyMatch?.[2]) ?? 'kg'

    // 2) product phrase (greedy: longest phrase that appears as whole words)
    let hit: { cod: string; phrase: string } | null = null
    for (const p of PHRASE_INDEX) {
      // whole-phrase match with punctuation-aware boundaries (handles "ficatei.")
      const re = new RegExp(`(^|[^a-z0-9])${p.phrase.replace(/ /g, '[^a-z0-9]+')}([^a-z0-9]|$)`)
      if (re.test(segment)) {
        hit = { cod: p.cod, phrase: p.phrase }
        break
      }
    }

    if (!hit) {
      if (!isNoiseOnly(segment)) {
        const leftover = cleanLeftover(segment)
        if (leftover) nemapate.push(leftover)
      }
      continue
    }

    const produs = byCod.get(hit.cod)
    if (!produs) continue

    // 3) convert quantity to kg by unit
    let cantitateKg = cantitate ?? 0
    let notaUM = ''
    if (um === 'tavă') {
      cantitateKg = (cantitate ?? 0) * TAVA_KG
      notaUM = ` · ${cantitate ?? 0} tăvi ≈ ${cantitateKg} kg`
    } else if (um === 'buc') {
      const avg = BUC_KG[produs.cod] ?? 1
      cantitateKg = Math.round((cantitate ?? 0) * avg * 10) / 10
      notaUM = ` · ${cantitate ?? 0} buc`
    }

    // 4) price (respect client pretSpecial when known)
    const pretKg = opts.pretSpecial?.[produs.id] ?? produs.pretKg

    const confidence = LOW_CONFIDENCE_WORDS.has(hit.phrase) ? 0.7 : 1

    linii.push({
      produsId: produs.id,
      cod: produs.cod,
      nume: produs.nume,
      cantitateKg,
      um,
      pretKg,
      matchedText: `${segment.trim()}${notaUM}`,
      confidence,
    })
  }

  return { linii, nemapate }
}
